if (!customElements.get('one-editorial-gallery')) {
  customElements.define('one-editorial-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.track = this.querySelector('.one-editorial-gallery__track');
      this.slides = [...this.querySelectorAll('[data-editorial-slide]')];
      if (!this.track || !this.slides.length) return;
      this.index = 0;
      this.previous = this.querySelector('[data-editorial-previous]');
      this.next = this.querySelector('[data-editorial-next]');
      this.previous.addEventListener('click', () => this.go(this.index - 1), options);
      this.next.addEventListener('click', () => this.go(this.index + 1), options);
      // Older merchant galleries may still use individual thumbnail controls.
      this.querySelectorAll('[data-editorial-go]').forEach(button => {
        button.addEventListener('click', () => this.go(Number(button.dataset.editorialGo)), options);
      });
      this.track.addEventListener('keydown', event => {
        const destinations = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in destinations)) return;
        event.preventDefault();
        this.go(destinations[event.key]);
      }, options);
      // The browser's native lazy threshold also fetches offscreen horizontal slides.
      // Keep those images inert until gallery interaction or entry into the track.
      this.track.addEventListener('pointerdown', () => this.prepareNeighbours(), { ...options, passive: true });
      if ('IntersectionObserver' in window) {
        this.visibility?.disconnect();
        this.visibility = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) this.loadSlide(this.slides.indexOf(entry.target)); });
        }, { root: this.track, threshold: 0.01 });
        this.slides.forEach(slide => this.visibility.observe(slide));
      }
      this.track.addEventListener('scroll', () => {
        this.loadSlide(Math.floor(this.track.scrollLeft / this.track.clientWidth));
        this.loadSlide(Math.ceil(this.track.scrollLeft / this.track.clientWidth));
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync(Math.round(this.track.scrollLeft / this.track.clientWidth)));
      }, { ...options, passive: true });
      this.resize = new ResizeObserver(() => this.go(this.index, false));
      this.resize.observe(this.track);
      this.classList.add('is-enhanced');
      this.querySelector('.one-editorial-gallery__controls').hidden = false;
      this.sync(0);
    }
    loadSlide(index) {
      const template = this.slides[index]?.querySelector('template[data-editorial-image]');
      if (!template) return;
      template.replaceWith(template.content.cloneNode(true));
    }
    prepareNeighbours() {
      this.loadSlide(this.index - 1);
      this.loadSlide(this.index + 1);
    }
    go(index, animate = true) {
      const target = Math.max(0, Math.min(index, this.slides.length - 1));
      this.loadSlide(target);
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    }
    sync(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      this.querySelectorAll('[data-editorial-go]').forEach((button, position) => {
        if (position === this.index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-editorial-count]');
      if (count) count.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
      this.querySelector('[data-editorial-status]').textContent = `Photograph ${this.index + 1} of ${this.slides.length}`;
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
